/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import DislikeButton from 'bundles/clips/components/DislikeButton';
import LikeButton from 'bundles/clips/components/LikeButton';
import { CMLUtils } from 'bundles/cml';
import {
  cancelDislikeItem,
  cancelLikeItem,
  dislikeItem,
  getItemFeedback,
  likeItem,
} from 'bundles/content-feedback/actions/ItemFeedbackActions';
import type { ItemType } from 'bundles/content-feedback/constants/ItemTypes';
import type LikeFeedback from 'bundles/content-feedback/models/LikeFeedback';


type PropsFromCaller = {
  courseId: string;
  itemFeedbackType: ItemType;
  itemId?: string;
  likes?: number;
};

type PropsFromGetContext = {
  executeAction: $TSFixMe;
};

type PropsFromStores = {
  likeFeedback: LikeFeedback;
  dislikeFeedback: LikeFeedback;
};

type Stores = {
  // @ts-expect-error ts-migrate(2749) FIXME: 'ItemFeedbackStoreClass' refers to a value, but is... Remove this comment to see the full error message
  ItemFeedbackStore: ItemFeedbackStoreClass;
};

export type Props = PropsFromCaller & PropsFromGetContext & PropsFromStores;

const clipEngagementActionsContainer = css({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
});

const likeContainer = css({
  display: 'flex',
  gap: '4px',
});

export const ClipEngagementActions = ({
  courseId,
  itemFeedbackType,
  itemId,
  likes,
  likeFeedback,
  dislikeFeedback,
  executeAction,
}: Props) => {
  const [numLikes, setNumLikes] = useState<number>(likes ?? 0);

  useEffect(() => {
    if (executeAction) {
      executeAction(getItemFeedback, {
        courseId,
        itemId,
      });
    }
  }, [courseId, itemId, executeAction]);

  const handleLike = () => {
    const comment = CMLUtils.create('');
    setNumLikes((prevNumLikes) => prevNumLikes + 1);
    executeAction(likeItem, {
      courseId,
      itemId,
      comment,
    });
  };

  const handleLikeCancel = () => {
    const { comment } = likeFeedback;
    setNumLikes((prevNumLikes) => prevNumLikes - 1);
    executeAction(cancelLikeItem, {
      courseId,
      itemId,
      comment,
    });
  };

  const handleDislike = () => {
    const comment = CMLUtils.create('');
    if (likeFeedback?.isSelected) {
      setNumLikes((prevNumLikes) => prevNumLikes - 1);
    }
    executeAction(dislikeItem, {
      courseId,
      itemId,
      comment,
    });
  };

  const handleDislikeCancel = () => {
    const { comment } = dislikeFeedback;
    executeAction(cancelDislikeItem, {
      courseId,
      itemId,
      comment,
    });
  };

  return (
    <div css={clipEngagementActionsContainer} data-testid="clip-engagement-actions">
      <div css={likeContainer}>
        <LikeButton
          selected={Boolean(likeFeedback?.isSelected)}
          onSelect={handleLike}
          onDeselect={handleLikeCancel}
          itemFeedbackType={itemFeedbackType}
          likes={numLikes}
        />
        <DislikeButton
          selected={Boolean(dislikeFeedback?.isSelected)}
          onSelect={handleDislike}
          onDeselect={handleDislikeCancel}
          itemFeedbackType={itemFeedbackType}
        />
      </div>
    </div>
  );
};

export default compose<Props, PropsFromCaller>(
  connectToStores<PropsFromCaller, PropsFromCaller, Stores>(['ItemFeedbackStore'], ({ ItemFeedbackStore }: Stores) => ({
    likeFeedback: ItemFeedbackStore.getLikeFeedback(),
    dislikeFeedback: ItemFeedbackStore.getDislikeFeedback(),
  })),
  getContext({
    executeAction: PropTypes.func.isRequired,
  })
)(ClipEngagementActions);
